import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, Wallet, CheckCircle2, Loader2 } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// ---- Config ----
const REMINDER_HOURS = [
  { v: 1, label: "1 hour before" },
  { v: 2, label: "2 hours before" },
  { v: 3, label: "3 hours before" },
  { v: 5, label: "5 hours before (recommended)" },
  { v: 12, label: "12 hours before" },
  { v: 24, label: "1 day before" },
];

const VENDOR_STAGES = [
  { key: "3d",  label: "3 days before due date" },
  { key: "1d",  label: "1 day before due date" },
  { key: "due", label: "On due date" },
];

const DEFAULT = {
  appointment_reminders_enabled: true,
  appointment_reminder_hours: 5,
  payment_reminders_enabled: false,
  payment_reminder_stages: ["3d", "1d", "due"],
  vendor_confirmations_enabled: true,
};

export default function Automation() {
  const nav = useNavigate();
  const [state, setState] = useState(null); // null = loading
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/automation");
      setState({ ...DEFAULT, ...data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load settings");
      setState({ ...DEFAULT });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Persist merged state via PATCH. All rules live in the same doc; a single PATCH per change.
  const persist = async (next, savingLabel) => {
    setSavingKey(savingLabel);
    try {
      const { data } = await api.patch("/settings/automation", next);
      setState({ ...DEFAULT, ...data });
      toast.success("Automation updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
      // revert
      load();
    } finally { setSavingKey(null); }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F2]">
      <Sidebar />
      <main className="nexa-compact lg:ml-[260px] min-h-screen">
        <div className="w-full max-w-[820px] mx-auto px-4 md:px-8 py-6 md:py-10">
          {/* Breadcrumb */}
          <button
            onClick={() => nav("/business-settings")}
            className="flex items-center gap-1.5 text-[10.5px] text-[#102C2D] hover:text-[#075B59] mb-4 transition-colors"
            data-testid="aut-back-btn"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={1.6} /> Business Settings
          </button>

          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
            className="mb-8"
          >
            <h1 className="mt-1 text-[18px] md:text-[19px] font-semibold tracking-[-0.02em] text-nexa-ink">
              Choose what Nexa does <span className="italic">on its own</span>.
            </h1>
            <p className="mt-1 text-[11px] text-nexa-mute max-w-[62ch]">
              Enable and tune the messages Nexa sends automatically. Changes save instantly.
            </p>
          </motion.header>

          {state === null ? (
            <div className="lux-card p-10 flex items-center justify-center text-nexa-mute text-[11px] gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.6} /> Loading settings…
            </div>
          ) : (
            <div className="space-y-4">
              {/* 1. Appointment Reminder */}
              <AutomationCard
                index={0}
                icon={Bell}
                title="Appointment Reminder"
                description="Nexa sends a friendly WhatsApp reminder to the customer before their appointment."
                enabled={state.appointment_reminders_enabled}
                onToggle={(v) => persist({ ...state, appointment_reminders_enabled: v }, "apt-toggle")}
                saving={savingKey === "apt-toggle" || savingKey === "apt-hours"}
                testId="aut-appointment"
              >
                <div className="text-[9.5px] tracking-[0.14em] uppercase font-semibold text-nexa-mute">
                  Reminder timing
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <select
                    disabled={!state.appointment_reminders_enabled}
                    value={state.appointment_reminder_hours}
                    onChange={(e) => persist(
                      { ...state, appointment_reminder_hours: Number(e.target.value) },
                      "apt-hours",
                    )}
                    className="lux-input !py-1.5 !text-[11px] max-w-[260px]"
                    data-testid="aut-appointment-timing"
                  >
                    {REMINDER_HOURS.map((h) => (
                      <option key={h.v} value={h.v}>{h.label}</option>
                    ))}
                  </select>
                </div>
              </AutomationCard>

              {/* 2. Vendor Payment Reminder */}
              <AutomationCard
                index={1}
                icon={Wallet}
                title="Vendor Payment Reminder"
                description="Nexa sends you a WhatsApp reminder before every vendor invoice is due — no scheduling required."
                enabled={state.payment_reminders_enabled}
                onToggle={(v) => persist({ ...state, payment_reminders_enabled: v }, "ven-toggle")}
                saving={savingKey === "ven-toggle" || savingKey?.startsWith("ven-stage-")}
                testId="aut-vendor-reminder"
              >
                <div className="text-[9.5px] tracking-[0.14em] uppercase font-semibold text-nexa-mute">
                  Reminder timings
                </div>
                <div className={`mt-2 space-y-1.5 ${state.payment_reminders_enabled ? "" : "opacity-50 pointer-events-none"}`}>
                  {VENDOR_STAGES.map((s) => {
                    const checked = (state.payment_reminder_stages || []).includes(s.key);
                    return (
                      <label
                        key={s.key}
                        className="flex items-center gap-2.5 py-1 cursor-pointer group"
                        data-testid={`aut-stage-${s.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const cur = new Set(state.payment_reminder_stages || []);
                            if (checked) cur.delete(s.key); else cur.add(s.key);
                            const next_stages = VENDOR_STAGES.map((x) => x.key).filter((k) => cur.has(k));
                            persist(
                              { ...state, payment_reminder_stages: next_stages },
                              `ven-stage-${s.key}`,
                            );
                          }}
                          className="w-3.5 h-3.5 accent-nexa-accent"
                        />
                        <span className="text-[11px] text-nexa-ink group-hover:text-nexa-accent transition-colors">
                          {s.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </AutomationCard>

              {/* 3. Vendor Payment Confirmation */}
              <AutomationCard
                index={2}
                icon={CheckCircle2}
                title="Vendor Payment Confirmation"
                description="When you mark an invoice Paid, Nexa instantly WhatsApps the vendor with confirmation."
                enabled={state.vendor_confirmations_enabled}
                onToggle={(v) => persist({ ...state, vendor_confirmations_enabled: v }, "conf-toggle")}
                saving={savingKey === "conf-toggle"}
                testId="aut-vendor-confirm"
              >
                <div className="rounded-xl bg-[#E8F4EF] border border-[#D9EDE5] px-3.5 py-2.5 flex items-start gap-2">
                  <span className="mt-0.5 w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                  <div className="text-[10.5px] text-nexa-mute">
                    Sent from the phone number saved on the vendor. If none is available, the message is skipped and logged.
                  </div>
                </div>
              </AutomationCard>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function AutomationCard({ icon: Icon, title, description, enabled, onToggle, saving, testId, children, index }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.05 + index * 0.05 }}
      className="lux-card !border-[#E0E8E3] !bg-white p-4 md:p-5 shadow-[0_4px_16px_rgba(16,44,45,0.035)] transition-all duration-300 hover:!border-[#B8DCD0] hover:!shadow-[0_8px_24px_rgba(7,91,89,0.06)]"
      data-testid={testId}
    >
      <div className="flex items-start gap-4">
        <div className="w-9 h-9 rounded-xl bg-[#E8F4EF] border border-[#D9EDE5] flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-[#075B59]" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-[14px] md:text-[15px] font-semibold text-nexa-ink tracking-[-0.01em]">
                {title}
              </h3>
              <p className="mt-1 text-[11px] leading-5 text-nexa-mute max-w-[54ch]">
                {description}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {saving && <Loader2 className="w-3 h-3 animate-spin text-nexa-mute" strokeWidth={1.6} />}
              <button
                type="button" role="switch" aria-checked={enabled}
                onClick={() => onToggle(!enabled)}
                disabled={saving}
                data-testid={`${testId}-toggle`}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-60 ${enabled ? "bg-[#075B59]" : "bg-[#D5DDD7]"}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`} />
              </button>
            </div>
          </div>

          <div className={`mt-4 rounded-xl border border-[#E0E8E3] bg-[#FDFCF8] p-3.5 transition-opacity ${enabled ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
            {children}
          </div>
        </div>
      </div>
    </motion.section>
  );
}
